import { useField, useFormikContext } from 'formik';
import React, { useEffect, useRef } from 'react';

import styled from '@emotion/styled';

import PlainStarIcon from '../Icons/PlainStarIcon';
import { FieldErrorInfo } from './FormField';

interface CustomStarInlineRadioCustomFieldGroupProps {
  children: React.ReactNode;
  name: string;
  label?: string;
  onChange?: (value: string) => void;
}

export const CustomStarInlineRadioCustomFieldGroup: React.FC<
  CustomStarInlineRadioCustomFieldGroupProps
> = ({ children, name, label, onChange }) => {
  const { errors, touched, values } = useFormikContext<any>();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (onChange && values[name] !== undefined) {
      onChange(String(values[name]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values[name]]);

  return (
    <RadioGroupWrapper>
      {label && <InputLabel>{label}</InputLabel>}
      <RadioGroup>
        {React.Children.map(children, (child: any) => {
          return React.cloneElement(child, {
            ...child.props,
          });
          // }
        })}
      </RadioGroup>

      {errors[name] && touched[name] ? (
        <CustomFieldErrorInfo>
          {(errors as any)[name].capitalize()}
        </CustomFieldErrorInfo>
      ) : null}
    </RadioGroupWrapper>
  );
};

export interface CustomStarRadioProps {
  label?: any;
  id?: string;
  name: string;
  children?: any;
  className?: string;
  value?: string;
  starNumber?: number;
  starCount?: number;
}

export const CustomStarRadio: React.FC<CustomStarRadioProps> = ({
  children,
  starNumber,
  starCount,
  className,
  ...props
}) => {
  const [field] = useField({ ...props, type: 'radio' });

  return (
    <RadioWrapper className={className}>
      <label>
        <input type='radio' {...field} {...props} />
        <RadioItemDiv>
          {starNumber}
          {starNumber ? (
            <>
              <RadioItemActiveIcon className='active'>
                <PlainStarIcon />
              </RadioItemActiveIcon>
              <RadioItemInactiveIcon className='inactive'>
                <PlainStarIcon />
              </RadioItemInactiveIcon>
            </>
          ) : (
            <All>All</All>
          )}
          {starNumber && starCount}
          {!starNumber && starCount && <StarCount>{starCount}</StarCount>}
          {/* {children} */}
        </RadioItemDiv>
      </label>
    </RadioWrapper>
  );
};

const RadioItemDiv = styled.div`
  display: flex;
  gap: 12px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 8px 20px;
  width: fit-content;
  border-radius: 24px;
  justify-content: space-between;
  align-items: center;
  font-weight: 400;
  font-size: 14px;
  line-height: 14px;
  height: 36px;
`;

export const InputLabel = styled.label`
  font-weight: 500;
  font-size: 12px;
  line-height: 12px;
  margin-bottom: 10px;
  color: black;
  user-select: none;
`;
const RadioItemActiveIcon = styled.div`
  display: none;
`;

const RadioItemInactiveIcon = styled.div``;

const RadioWrapper = styled.div`
  & > label {
    width: 100%;
    display: grid;

    & > * {
      grid-column: 1;
      grid-row: 1;
    }
  }

  & > label input[type='radio'] {
    visibility: hidden;
  }

  & > label input[type='radio']:checked ~ div > .active {
    color: #a1a1a1;
  }

  & > label input[type='radio']:checked ~ div > .inactive {
    color: white;
  }

  & > label input[type='radio']:checked ~ div {
    border: 1px solid ${({ theme }) => theme.palette.blueColor};
    color: white;
    font-weight: 600;
    background-color: ${({ theme }) => theme.palette.blueColor};
  }

  & > * {
    cursor: pointer;
  }
`;

const RadioGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* border: 1px solid red; */
  padding: 0px;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-direction: row;
  /* border: 1px solid blue; */

  & > * {
  }
`;

const CustomFieldErrorInfo = styled(FieldErrorInfo)`
  margin-top: 0px;
`;

const All = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 14px;
`;

const StarCount = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 14px;
  border-radius: 14px;
  padding: 4px 12px;
  background-color: #ffffff47;
`;
